<script lang="ts">
	import Carousel from '$lib/carousel.svelte';
	import PageGrid16 from '$lib/storyblok/components/pageGrid16.svelte';
	import SbRichtext from '$lib/storyblok/components/sbRichtext.svelte';
	import type { SbImage } from '$lib/storyblok/types';
	import ArrowLeft from '$lib/icons/arrowLeft.svelte';
	import ArrowRight from '$lib/icons/arrowRight.svelte';
	import type { EmblaCarouselType } from 'embla-carousel';
	import { onMount } from 'svelte';
	import SimpleGalleryIndicator from '$lib/simpleGalleryIndicator.svelte';
	import Button from '$lib/buttons/v3/button.svelte';
	import type { ISbRichtext, ISbStoryData } from '@storyblok/js';
	import { editable } from '$lib/storyblok/editable';
	import Blok from '$lib/storyblok/blok.svelte';
	import { device } from '$lib/state/device.svelte';

	interface Props {
		content: ISbStoryData<{
			headline: ISbRichtext;
			description: ISbRichtext;
			gallery_items: Array<
				ISbStoryData<{
					text_color: string;
					title: string;
					image: SbImage;
					caption: string;
				}>['content']
			>;
		}>['content'];
	}

	let { content }: Props = $props();

	let width = $state(1400);

	let currentIndex = $state(Math.floor(content.gallery_items.length * 0.5));
	let items = $state(content.gallery_items);
	let scrollProgress = $state(0);

	let embla = $state<EmblaCarouselType>();

	onMount(() => {
		scrollProgress = currentIndex / items.length;

		return () => embla?.destroy();
	});

	function enableLooping() {
		if (!embla) return;

		// duplicate the items if embla isn't able to create a loop
		if (!embla.internalEngine().slideLooper.canLoop()) {
			items = [...content.gallery_items, ...content.gallery_items];
			embla.reInit();
			scrollProgress = currentIndex / items.length;
		}
	}

	$effect(() => {
		if (embla) {
			enableLooping();

			embla.on('scroll', (e) => {
				scrollProgress = e.scrollProgress();
			});
		}
	});

	function getOffset(index: number, totalSlides: number, scrollProgress: number) {
		// Normalize scrollProgress to range between 0 and totalSlides
		const progress = scrollProgress * totalSlides;

		// Calculate the difference between the slide index and the normalized progress
		let diff = index - progress;

		// Handle wrapping (carousel looping) by adjusting for slides that are far
		if (diff > totalSlides / 2) {
			diff -= totalSlides;
		}
		if (diff < -totalSlides / 2) {
			diff += totalSlides;
		}

		// Return the offset as a proportion (-1 to 1) for parallax effect
		return diff / (totalSlides / 2);
	}

	let cssVars = $derived(`--scroll-progress: ${scrollProgress}; --card-width: ${width}px`);
	let realCurrentIndex = $derived(currentIndex % content.gallery_items.length);
</script>

<svelte:window onresize={() => enableLooping()} />

<section
	use:editable={content}
	class="bg-white pb-8 pt-16 text-brown-1 lg:pb-16 lg:pt-32"
	style={cssVars}
>
	<PageGrid16 gridClass="items-center" class="mb-6 lg:mb-12" bind:gridWidth={width}>
		<h1
			class="col-span-full text-[44px]/[108%] tracking-[-.44px] max-lg:mb-2 max-lg:px-5 max-lg:text-center lg:col-start-2 lg:col-end-8 lg:text-[64px]/[110%] lg:tracking-[-.64px]"
		>
			<SbRichtext text={content.headline} />
		</h1>
		<div
			class="col-span-full text-balance text-[16px]/[150%] tracking-[-.16px] text-brown-4 max-lg:px-5 max-lg:text-center lg:col-start-9 lg:col-end-[16] lg:text-[24px]/[140%] lg:tracking-[-.24px]"
		>
			<SbRichtext text={content.description} />
		</div>
	</PageGrid16>

	<div class="relative">
		<Carousel loop bind:currentIndex class="flex" bind:embla draggable={!device.data.isDesktop}>
			{#each items as item, i}
				<div class="card-wrapper mx-1.5 flex-none basis-full lg:mx-2.5">
					<Blok content={item} offset={getOffset(i, items.length, scrollProgress)} />
				</div>
			{/each}
		</Carousel>

		<div class="absolute inset-y-0 left-0 flex h-full items-center p-5 max-lg:hidden">
			<Button
				class="btn-white shadow-[0_2px_8px_0_rgba(0,0,0,0.2)]"
				onclick={() => embla?.scrollPrev()}
				title="Previous slide"
			>
				<ArrowLeft class="h-6 w-6" />
			</Button>
		</div>
		<div class="absolute inset-y-0 right-0 flex h-full items-center justify-end p-5 max-lg:hidden">
			<Button
				class="btn-white shadow-[0_2px_8px_0_rgba(0,0,0,0.2)]"
				onclick={() => embla?.scrollNext()}
				title="Next slide"
			>
				<ArrowRight class="h-6 w-6" />
			</Button>
		</div>
	</div>

	<div class="info-wrapper mx-auto flex flex-col items-center gap-y-6 pt-6 lg:pb-16">
		<p
			class="text-balance text-center text-[16px]/[140%] tracking-[-.16px] lg:text-[20px]/[140%] lg:tracking-[-.2px]"
		>
			{content.gallery_items[realCurrentIndex]?.caption}
		</p>

		<SimpleGalleryIndicator
			onchange={(index) => embla?.scrollTo(index)}
			count={content.gallery_items.length}
			active={realCurrentIndex}
		/>
	</div>
</section>

<style>
	section {
		/* total grid width - 1 col */
		--card-wrapper-width: calc(var(--card-width) / 4 * 3);

		@media (min-width: 1024px) {
			/* lg: total grid width - 2 cols */
			--card-wrapper-width: calc(var(--card-width) / 16 * 14);
		}
	}

	div.card-wrapper,
	div.info-wrapper {
		max-width: var(--card-wrapper-width);
	}
</style>
